// ─── Prenotazione e disdetta, fase B: le regole senza soldi (28/09/2026) ─────
//
// 🗣️ Dalla specifica «Prenotazione e disdetta» v3 del 27/09, che SOSTITUISCE la
// caparra e l'acconto della sera: si prenota liberamente e si disdice gratis
// fino a 48 ore prima. Una disdetta più tardi, uno spostamento più tardi o un
// no-show sono un'INFRAZIONE dell'organizzatore; alla prima si prenota con una
// carta a garanzia (fase C), e dopo 5 partite regolari si torna liberi.
//
// Mockup «Prenotazione e disdetta», approvato il 28/09 (*«mockup approvato,
// scelte consigliate, procedi»* ⇒ A · A · A):
//   1A — l'interruttore ha tre posizioni: spento · solo registro · acceso.
//        «Acceso» arriva con la carta (fase C); nel «solo registro» le
//        infrazioni si scrivono e le vede la Segreteria, ma non contano MAI
//        contro il socio: contano solo quelle dopo l'avvio;
//   2A — l'acconto della sera si spegne adesso;
//   3A — la Segreteria che disdice o sposta a meno di 48 ore chiede «chi lo
//        chiede?»: solo «il socio» fa nascere un'infrazione.
//
// Qui stanno le regole PURE. Le scritture stanno nell'API (`modules/disdette/`).
// ⭐ Lo stato del socio non si scrive da nessuna parte: si CALCOLA, così non
// c'è niente da tenere allineato.
import { z } from 'zod';
import { dataSchema, oraSchema } from './circolo.js';
/**
 * `2026-09-29` → «martedì 29 settembre», come `giornoPerEsteso` di `avvisi.ts`.
 * ⚠️ Non lo si importa di là: `avvisi.js` → `calendario.js` → `organizzatore.js`
 * → qui farebbe un giro, e `organizzatore.js` legge lo schema delle infrazioni.
 */
const GIORNI_MINUSCOLI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MESI_MINUSCOLI = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
];
function giornoPerEsteso(data) {
    const d = new Date(`${data}T12:00:00Z`);
    return `${GIORNI_MINUSCOLI[d.getUTCDay()]} ${d.getUTCDate()} ${MESI_MINUSCOLI[d.getUTCMonth()]}`;
}
// ── Le impostazioni ─────────────────────────────────────────────────────────
export const STATI_DELLE_REGOLE = ['spento', 'registro', 'acceso'];
/**
 * ⛔ «Acceso» si può scegliere solo quando c'è la carta (fase C). Prima, un
 * socio in obbligo carta non avrebbe la strada per salvarla, e resterebbe
 * chiuso fuori (scelta 1A del mockup).
 */
export const ACCESO_DISPONIBILE = false;
export const impostazioniDisdetteSchema = z.object({
    stato: z.enum(STATI_DELLE_REGOLE),
    /**
     * L'istante in cui le regole si sono ACCESE (non il «solo registro»). Le
     * infrazioni contano da qui: *«il comportamento precedente dei soci non
     * conta»* (§ 11). Nullo finché non si accende.
     */
    avvio: z.string().nullable(),
    /** Si disdice gratis fino a tante ore prima dell'inizio (§ 2, regola 2). Una fonte sola. */
    oreDisdetta: z
        .number()
        .int()
        .min(1)
        .max(24 * 14),
    /**
     * 🎓 25/10/2026 (bot, 🗣️ *«vanno bene 6 ore prima dell'inizio della lezione. Dopodiché entro quel tempo la devi
     * pagare, perché mi sembra corretto verso il maestro»* · *«Non si può disdire entro le 6 ore e non si può spostare»*):
     * le lezioni, gli allenamenti e le partite tattiche si disdicono gratis e si spostano fino a tante ore prima
     * dell'inizio, a parte dalle partite. Chi aveva già le impostazioni salvate le legge a 6.
     */
    oreDisdettaLezioni: z
        .number()
        .int()
        .min(1)
        .max(24 * 14)
        .default(6),
    /** Quante infrazioni fanno scattare l'obbligo carta (§ 2, regola 4). */
    infrazioniPerObbligo: z.number().int().min(1).max(10),
    /** Quante partite regolari da organizzatore per tornare liberi (§ 3.4). */
    partiteRegolari: z.number().int().min(1).max(50),
    /** § 4.4 — gli avvisi sulla composizione della partita. */
    avvisi: z.object({
        /** «Siete in N su 4» tante ore prima, se la partita è incompleta. */
        oreIncompleta: z
            .number()
            .int()
            .min(24)
            .max(24 * 7),
        vigiliaAlle: oraSchema,
    }),
    /** Fase C: l'addebito del residuo, tanti minuti dopo la fine. Si salva, ma non fa ancora niente. */
    ritardoResiduoMinuti: z
        .number()
        .int()
        .min(0)
        .max(24 * 60),
    /** Fase C: quanto vale il link per salvare la carta. */
    minutiLinkCarta: z
        .number()
        .int()
        .min(5)
        .max(24 * 60),
    /** Fase D: il Borsellino scade tanti mesi dopo l'ultima ricarica. */
    mesiBorsellino: z.number().int().min(1).max(60),
});
export const IMPOSTAZIONI_DISDETTE_DI_PARTENZA = {
    stato: 'spento',
    avvio: null,
    oreDisdetta: 48,
    oreDisdettaLezioni: 6,
    infrazioniPerObbligo: 1,
    partiteRegolari: 5,
    avvisi: { oreIncompleta: 72, vigiliaAlle: '18:00' },
    ritardoResiduoMinuti: 60,
    minutiLinkCarta: 15,
    mesiBorsellino: 12,
};
/** Quello che la Segreteria scrive: tutto tranne l'avvio, che lo fissa il gestionale quando si accende. */
export const scriviImpostazioniDisdetteSchema = impostazioniDisdetteSchema
    .omit({ avvio: true })
    .refine((v) => ACCESO_DISPONIBILE || v.stato !== 'acceso', {
    message: '«Acceso» si potrà scegliere quando i soci potranno lasciare la carta a garanzia.',
    path: ['stato'],
});
/** Le regole registrano qualcosa (solo registro o accese)? */
export function regoleVive(imp) {
    return imp.stato !== 'spento';
}
/** Le regole parlano ai soci? Solo accese: nel «solo registro» i soci non ricevono niente. */
export function regoleAccese(imp) {
    return imp.stato === 'acceso';
}
/**
 * ⭐ Fino a quando si disdice gratis: l'inizio meno `ore`, **sull'orologio di
 * Roma**. È lo stesso conto di `termineDellaDisdetta` (API), che lo fa in SQL.
 *
 * ⚖️ Perché l'orologio e non gli istanti (§ 10, caso 23): una partita di
 * domenica 25 ottobre alle 19:00 si disdice gratis fino a venerdì 23 alle
 * 19:00, anche se quella notte l'ora è tornata indietro. È l'ora che il socio
 * legge, e deve essere quella giusta.
 */
export function termineDellaDisdetta(inizio, ore) {
    const [y, m, g] = inizio.data.split('-').map(Number);
    const [h, min] = inizio.ora.split(':').map(Number);
    const t = new Date(Date.UTC(y, m - 1, g, h, min) - ore * 3_600_000);
    const due = (n) => String(n).padStart(2, '0');
    return {
        data: `${t.getUTCFullYear()}-${due(t.getUTCMonth() + 1)}-${due(t.getUTCDate())}`,
        ora: `${due(t.getUTCHours())}:${due(t.getUTCMinutes())}`,
    };
}
/** Il termine è passato? `adesso` è l'orologio di Roma in questo momento. */
export function dentroLeOre(adesso, inizio, ore) {
    const t = termineDellaDisdetta(inizio, ore);
    return `${adesso.data} ${adesso.ora}` >= `${t.data} ${t.ora}`;
}
/** L'orologio di Roma di un istante. */
export function oraDiRoma(istante) {
    const d = typeof istante === 'string' ? new Date(istante) : istante;
    const parti = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(d);
    const v = (t) => parti.find((p) => p.type === t)?.value ?? '00';
    return { data: `${v('year')}-${v('month')}-${v('day')}`, ora: `${v('hour')}:${v('minute')}` };
}
/** «giovedì 1 ottobre alle 19:00». */
export function termineAParole(t) {
    return `${giornoPerEsteso(t.data)} alle ${t.ora}`;
}
// ── Le infrazioni ───────────────────────────────────────────────────────────
/**
 * - `late_cancel`: disdetta a meno di `oreDisdetta` dall'inizio (dal bot, per telefono, o
 *   dalla Segreteria «perché lo chiede il socio»);
 * - `no_show`: nessuno si è presentato, segnato dalla Segreteria;
 * - `late_move`: spostata di giorno o di ora a meno di `oreDisdetta` (§ 3.2). Il solo
 *   cambio di campo è sempre libero.
 */
export const TIPI_DI_INFRAZIONE = ['late_cancel', 'no_show', 'late_move'];
export const ETICHETTE_INFRAZIONE = {
    late_cancel: '⏰ Disdetta tardiva',
    no_show: '🚫 No-show',
    late_move: '🔀 Spostata tardi',
};
export const infrazioneSchema = z.object({
    id: z.uuid(),
    bookingId: z.uuid(),
    kind: z.enum(TIPI_DI_INFRAZIONE),
    /** Data e ora della partita (Roma). */
    date: dataSchema,
    time: oraSchema,
    courtName: z.string(),
    recordedAt: z.string(),
    /** Chi l'ha registrata dalla Segreteria; nullo se dal bot. */
    recordedBy: z.string().nullable(),
    /** Vero se è nata a regole accese; falsa nel «solo registro», dove non conta mai. */
    counted: z.boolean(),
    annulled: z.object({ at: z.string(), by: z.string().nullable(), reason: z.string() }).nullable(),
});
/**
 * ⭐ **LO STATO DEL SOCIO, IN UNA FUNZIONE PURA.** Si percorre la storia in
 * ordine di tempo:
 *   - un'infrazione **contata**, non annullata, dopo l'avvio: se il socio è
 *     libero la si conta, e alla soglia scatta l'obbligo; se è già in obbligo
 *     il contatore delle partite riparte da zero (§ 3.3);
 *   - una partita regolare da organizzatore (giocata, niente no-show né
 *     infrazione): in obbligo fa +1, e alla soglia si torna liberi (§ 3.4);
 *   - la Segreteria che toglie l'obbligo: si torna liberi, da capo.
 *
 * Le disdette fatte per tempo non passano di qui: non contano né in positivo
 * né in negativo (§ 10, caso 11).
 *
 * @param partite gli istanti di FINE delle partite regolari da organizzatore.
 * @param rimozioni gli istanti in cui la Segreteria ha tolto l'obbligo.
 */
export function statoDelSocio(infrazioni, partite, rimozioni, imp) {
    const servono = imp.partiteRegolari;
    const libero = { stato: 'libero', regolari: 0, servono, dal: null };
    // 🔕 Spente o nel «solo registro», nessuno cambia stato (scelta 1A).
    if (imp.stato !== 'acceso' || imp.avvio === null)
        return libero;
    const avvio = Date.parse(imp.avvio);
    const fatti = [
        ...infrazioni
            .filter((i) => i.counted && !i.annulled && Date.parse(i.at) >= avvio)
            .map((i) => ({ t: Date.parse(i.at), cosa: 'infrazione', at: i.at })),
        ...partite
            .filter((p) => Date.parse(p) >= avvio)
            .map((p) => ({ t: Date.parse(p), cosa: 'partita', at: p })),
        ...rimozioni.map((r) => ({ t: Date.parse(r), cosa: 'rimozione', at: r })),
    ].sort((a, b) => a.t - b.t);
    let stato = 'libero';
    let regolari = 0;
    let infrazioniDaLibero = 0;
    let dal = null;
    for (const f of fatti) {
        if (f.cosa === 'rimozione') {
            stato = 'libero';
            regolari = 0;
            infrazioniDaLibero = 0;
            dal = null;
        }
        else if (f.cosa === 'infrazione') {
            if (stato === 'obbligo_carta') {
                regolari = 0;
            }
            else if (++infrazioniDaLibero >= imp.infrazioniPerObbligo) {
                stato = 'obbligo_carta';
                regolari = 0;
                infrazioniDaLibero = 0;
                dal = f.at;
            }
        }
        else if (stato === 'obbligo_carta' && ++regolari >= servono) {
            stato = 'libero';
            regolari = 0;
            dal = null;
        }
    }
    return { stato, regolari, servono, dal };
}
/** Quello che la Segreteria vede nella scheda del socio. */
export const statoDisdetteDelSocioSchema = z.object({
    /** Le regole com'erano in quel momento: «spento», «registro», «acceso». */
    rules: z.enum(STATI_DELLE_REGOLE),
    status: z.enum(['libero', 'obbligo_carta']),
    regularGames: z.number().int(),
    needed: z.number().int(),
    since: z.string().nullable(),
    infractions: z.array(infrazioneSchema),
    /** L'ultima volta che la Segreteria ha tolto l'obbligo. */
    removed: z.object({ at: z.string(), by: z.string().nullable(), reason: z.string() }).nullable(),
});
export const motivoSchema = z.object({
    reason: z.string().trim().min(3, 'Scrivi il motivo: resta nella storia').max(500),
});
/** Quando la Segreteria disdice o sposta una partita dentro le ore: chi lo chiede (scelta 3A). */
export const CHI_LO_CHIEDE = ['socio', 'circolo'];
// ── Il ponte: le regole viste dal bot ───────────────────────────────────────
/**
 * `GET /consumer/booking-rules` — quello che il bot deve sapere per dire al
 * socio fino a quando disdice gratis. ⛔ Con `active` falso il bot non dice
 * niente di nuovo: nel «solo registro» i soci non ricevono niente.
 */
export const regoleDelPonteSchema = z.object({
    active: z.boolean(),
    status: z.enum(['libero', 'obbligo_carta']),
    regularGames: z.number().int(),
    needed: z.number().int(),
    cancelHours: z.number().int(),
    /** Per una partita precisa (`date` e `time` nella domanda): fino a quando si disdice gratis. */
    freeUntil: z.object({ date: dataSchema, time: oraSchema, text: z.string() }).nullable(),
    /** Il termine è già passato? */
    withinHours: z.boolean().nullable(),
});
// ── Le frasi per il socio (🌞 mai una frase negativa) ────────────────────────
export function testoPrimaInfrazione(p) {
    const ciao = p.nome ? `Ciao ${p.nome}, ` : 'Ciao, ';
    const partita = `la partita di ${giornoPerEsteso(p.date)} alle ${p.time}`;
    const cosa = p.kind === 'no_show'
        ? `risulta che nessuno si è presentato alla partita di ${giornoPerEsteso(p.date)} alle ${p.time}.`
        : p.kind === 'late_move'
            ? `${partita} è stata spostata a meno di ${p.ore} ore dall’inizio.`
            : `${partita} è stata disdetta a meno di ${p.ore} ore dall’inizio.`;
    return (`${ciao}${cosa}\n\n` +
        `Da ora, per prenotare, ti chiederò una carta a garanzia: è questione di un attimo, e dopo ${p.servono} partite regolari torni a prenotare liberamente.\n\n` +
        'Se pensi che ci sia un errore, scrivi alla Segreteria.');
}
/** Un'infrazione mentre è già in obbligo: il contatore riparte (l'addebito arriva con la fase C). */
export function testoInfrazioneInObbligo(p) {
    const cosa = p.kind === 'no_show'
        ? `Risulta che nessuno si è presentato alla partita di ${giornoPerEsteso(p.date)} alle ${p.time}`
        : `La partita di ${giornoPerEsteso(p.date)} alle ${p.time} è stata ${p.kind === 'late_move' ? 'spostata' : 'disdetta'} tardi`;
    return `${cosa}: le partite regolari ripartono da 0 su ${p.servono}. Se pensi che ci sia un errore, scrivi alla Segreteria.`;
}
export function testoProgresso(regolari, servono) {
    const mancano = servono - regolari;
    const ancora = mancano === 1 ? 'Ancora 1' : `Ancora ${mancano}`;
    return `🎾 Partite regolari: ${regolari} su ${servono}. ${ancora} e torni a prenotare senza carta.`;
}
export function testoLibero(servono) {
    return `🎉 Hai completato ${servono} partite regolari: da oggi prenoti di nuovo senza carta.`;
}
//# sourceMappingURL=disdette.js.map